import { Failure, Success } from "@/types/result";
import ResponseError from "./responseError";

const httpMethods = {
	get: "GET",
	post: "POST",
	patch: "PATCH",
	delete: "DELETE",
} as const;

class Fetcher {
	/**
	 * GET リクエストを送信します。
	 * @param url URL
	 * @param token アクセストークン
	 * @param options fetch オプション
	 * @returns レスポンス
	 */
	async get<T>(url: string, token?: string, options?: RequestInit) {
		const response = await this.request<T>(url, token, {
			...options,
			method: httpMethods.get,
		});
		return response as Success<T> | Failure<Error>;
	}

	/**
	 * POST リクエストを送信します。
	 * @param url URL
	 * @param token アクセストークン
	 * @param body リクエストボディ
	 * @param options fetch オプション
	 * @returns レスポンス
	 */
	async post(
		url: string,
		body?: object,
		token?: string,
		options?: RequestInit,
	) {
		const response = await this.request(url, token, {
			...options,
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(body),
			method: httpMethods.post,
		});
		return response as Success<null> | Failure<Error>;
	}

	/**
	 * PATCH リクエストを送信します。
	 * @param url URL
	 * @param token アクセストークン
	 * @param body リクエストボディ
	 * @param options fetch オプション
	 * @returns レスポンス
	 */
	async patch(
		url: string,
		body: object,
		token?: string,
		options?: RequestInit,
	) {
		const response = await this.request(url, token, {
			...options,
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(body),
			method: httpMethods.patch,
		});
		return response as Success<null> | Failure<Error>;
	}

	/**
	 * DELETE リクエストを送信します。
	 * @param url URL
	 * @param token アクセストークン
	 * @param options fetch オプション
	 * @returns レスポンス
	 */
	async delete(url: string, token?: string, options?: RequestInit) {
		const response = await this.request(url, token, {
			...options,
			method: httpMethods.delete,
		});
		return response as Success<null> | Failure<Error>;
	}

	/**
	 * HTTP リクエストを送信します。
	 * @param url URL
	 * @param token アクセストークン
	 * @param options fetch オプション
	 * @returns レスポンス
	 */
	async request<T>(url: string, token?: string, options?: RequestInit) {
		try {
			const headers: any = {
				...options?.headers,
			};
			if (token) headers.Authorization = token;

			const response = await fetch(url, {
				...options,
				headers,
				next: {
					revalidate: 0,
				},
			});
			if (response.status === 204) return new Success(null);
			if (!response.ok)
				throw new ResponseError(response.statusText, response);
			const json = await response.json();
			return new Success<T>(json);
		} catch (e: unknown) {
			if (e instanceof Error) return new Failure(e);
			return new Failure(new Error("Unhandled error has occured."));
		}
	}
}

export const fetcher = new Fetcher();
